'use client'

import { useQuery } from '@tanstack/react-query'
import { RefreshCw, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'

const DISMISS_KEY = 'version-watcher-dismissed'

function readDismissed(): string | null {
	// 'use client' components are still server-rendered for the initial HTML, so
	// this initializer runs on the server too — the window guard is reachable and
	// required (sessionStorage doesn't exist there).
	if (typeof window === 'undefined') return null
	try {
		return sessionStorage.getItem(DISMISS_KEY)
	} catch {
		return null
	}
}

function writeDismissed(buildId: string) {
	try {
		sessionStorage.setItem(DISMISS_KEY, buildId)
	} catch {
		// sessionStorage can throw in privacy modes — dismiss is best-effort.
	}
}

// Clear Cache Storage + unregister service workers (best-effort), then hard-reload
// so the new BUILD_ID's hashed chunks come from the network, not a stale cache.
async function clearCachesAndReload() {
	try {
		if (typeof caches !== 'undefined') {
			const keys = await caches.keys()
			await Promise.all(keys.map((k) => caches.delete(k)))
		}
	} catch {
		// Cache Storage unavailable / blocked — fall through to reload.
	}
	try {
		if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
			const regs = await navigator.serviceWorker.getRegistrations()
			await Promise.all(regs.map((r) => r.unregister()))
		}
	} catch {
		// Service worker API unavailable / blocked — fall through to reload.
	}
	window.location.reload()
}

// Polls the server's build id and surfaces a small corner prompt when the
// running deployment no longer matches the one this tab loaded with. The
// loaded-with id is passed from the (server-rendered) layout, so it's captured
// at page load and never changes for the life of the tab.
export function VersionWatcher({ buildId }: { buildId: string }) {
	// We remember which *server* build id the user dismissed, not a bare boolean.
	// That makes the dismiss sticky across soft navigations / remounts (via
	// sessionStorage) yet self-clearing on the next deploy: once the server
	// reports a newer build id, the stored value no longer matches and the
	// prompt reappears.
	const [dismissedBuildId, setDismissedBuildId] = useState<string | null>(readDismissed)
	const [refreshing, setRefreshing] = useState(false)

	const { data } = useQuery({
		queryKey: ['build-version'],
		queryFn: async () => {
			const res = await fetch('/api/version', { cache: 'no-store' })
			if (!res.ok) throw new Error('version check failed')
			return (await res.json()) as { buildId: string }
		},
		refetchInterval: 60_000,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		// Match the poll interval so a focus-refetch only hits the network when the
		// cached result is older than a minute — avoids a burst of calls when the
		// user alt-tabs frequently.
		staleTime: 60_000,
		retry: false,
	})

	const serverBuildId = data?.buildId

	const dismiss = useCallback(() => {
		if (!serverBuildId) return
		writeDismissed(serverBuildId)
		setDismissedBuildId(serverBuildId)
	}, [serverBuildId])

	const refresh = useCallback(() => {
		setRefreshing(true)
		void clearCachesAndReload()
		// Safety net: if the browser blocks the reload (rare), don't leave the
		// button spinning forever — re-enable it after a few seconds.
		window.setTimeout(() => setRefreshing(false), 5_000)
	}, [])

	// 'development' is the dev/unknown sentinel — never prompt on it.
	const outdated = buildId !== 'development' && !!serverBuildId && serverBuildId !== buildId
	const dismissed = !!serverBuildId && dismissedBuildId === serverBuildId

	if (!outdated || dismissed) return null

	return (
		// z-index sits just under the cookie-consent banner (z-[9999], legally
		// required) but above everything else, so this informational toast is never
		// hidden behind ordinary page content / modals.
		<output className="fixed bottom-4 right-4 z-[9998] flex items-center gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg animate-in fade-in slide-in-from-bottom-2">
			<span className="text-sm font-medium">New version available</span>
			<Button size="sm" onClick={refresh} disabled={refreshing}>
				<RefreshCw aria-hidden="true" className={refreshing ? 'animate-spin' : undefined} />
				{refreshing ? 'Refreshing…' : 'Refresh'}
			</Button>
			<button
				type="button"
				aria-label="Dismiss"
				onClick={dismiss}
				disabled={refreshing}
				className="rounded-md p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
			>
				<X className="size-4" aria-hidden="true" />
			</button>
		</output>
	)
}
